import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Card, CardBody, Col, Container, Row } from 'reactstrap';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import ReportViewer from '../ReportViewer';
import ReportHeader from '../Utils/ReportHeader';
import ReportFooter from '../Utils/ReportFooter';
import ReportFilterInfo from '../Utils/ReportFilterInfo';
import { generalStyles } from '../Utils/ReportStyling';
import Breadcrumbs from '../../CustomControls/Breadcrumb';
import { getAllRptClientProfileData, setOnSearchChange } from '../../../store/actions/ReportListing/rptClientProfileListing';
import Dropdown from '../../CustomControls/Dropdown';
import { STATUSES } from '../../../constants/filterConstants';
import { CSVLink } from "react-csv";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';
import LoadingSpinner from '../../CustomControls/Spinner';

const styles = StyleSheet.create({
  clientCode: {
    width: '8%'
  },
  clientName: {
    width: '22%'
  },
  clientTypeName: {
    width: '10%'
  },
  abn: {
    width: '10%'
  },
  debtorCode: {
    width: '10%'
  },
  deliveryCharge: {
    width: '8%'
  },
  primaryContactPerson: {
    width: '12%'
  },
  primaryContactEmail: {
    width: '13%'
  },
  status: {
    width: '7%'
  }
});

const exportHeader = [
  { label: "Code", key: "clientCode" },
  { label: "Client Name", key: "clientName" },
  { label: "Client Type", key: "clientTypeName" },
  { label: "ABN", key: "abn" },
  { label: "Debtor Code", key: "debtorCode" },
  { label: "Delivery Charge", key: "deliveryCharge" },
  { label: "Contact Person", key: "primaryContactPerson" },
  { label: "Contact Email", key: "primaryContactEmail" },
  { label: "Status", key: "isActive" },
];

const ClientProfileListingRpt = props => {
  const dispatch = useDispatch();
  const [filterStatus, setFilterStatus] = useState({ field: 'isactive', value: 'all', label: 'All' });
  const [filterClientType, setFilterClientType] = useState({ field: 'clienttypeid', value: 'all', label: 'All' });
  const rptData = useSelector(state => state.rptClientProfileListing);

  const isLoading = rptData.isLoading || false;
  const data = rptData.rptList;
  const filterData = rptData.filterData;
  const reportHeading = 'Client Profile Listing';

  useEffect(() => {
    dispatch(getAllRptClientProfileData(rptData.filterOptions.filterBy, rptData.filterOptions.sortBy, true));
  }, []);

  const breadCrumbPaths = [
    { id: 1, title: 'Home', path: '/' },
    { id: 2, title: `${reportHeading} Report`, path: '/reports/client-profile-listing', isCurrent: true }
  ];

  const dropdownChange = (field, selectionOption, callSetMethod) => {
    selectionOption.field = field;
    callSetMethod(selectionOption);
  };

  const setLocalChange = currentState => {
    dispatch(setOnSearchChange(currentState));
  };

  const onGenerateHandler = () => {
    let filterSearchList = rptData.filterOptions.filterBy || [];

    filterSearchList = getFilterData(filterSearchList, filterStatus);

    filterSearchList = getFilterData(filterSearchList, filterClientType);

    setLocalChange({
      ...rptData,
      data: { ...rptData.filterOptions, filterBy: filterSearchList }
    });

    dispatch(getAllRptClientProfileData(rptData.filterOptions.filterBy, rptData.filterOptions.sortBy));
  };

  const getFilterData = (filterSearchList, filterObj) => {
    const objIndex = filterSearchList.findIndex(x => x.name.toLowerCase() === filterObj.field);
    if (objIndex !== -1) {
      filterSearchList[objIndex].value = filterObj.value;
      filterSearchList[objIndex].valueName = filterObj.label;
    } else {
      filterSearchList.push({ name: filterObj.field, value: filterObj.value, valueName: filterObj.label });
    }
    return filterSearchList;
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={`${reportHeading} Report`} paths={breadCrumbPaths} />
          <Row>
            <Col xs="12">
              <Card>
                <CardBody>
                  <Row className="mb-2 buttonrow">
                    <Col sm="3">
                      <div className="mb-3">
                        <Dropdown
                          placeholder="Select Status"
                          label="Status"
                          name="isactive"
                          options={STATUSES}
                          isMulti={false}
                          value={STATUSES.find(x => x.value === filterStatus.value)}
                          onChange={val => dropdownChange('isactive', val, setFilterStatus)}
                          validate={{
                            required: { value: false }
                          }}
                        />
                      </div>
                    </Col>
                    <Col sm="3">
                      <div className="mb-3">
                        <Dropdown
                          placeholder="All"
                          label="Client Type"
                          name="clienttypeid"
                          options={filterData.clientTypes}
                          isMulti={false}
                          value={filterData.clientTypes.find(x => x.value === filterClientType.value)}
                          onChange={val => dropdownChange('clienttypeid', val, setFilterClientType)}
                          validate={{
                            required: { value: false }
                          }}
                        />
                      </div>
                    </Col>
                    <Col sm="4">
                      <div className="text-sm-start">
                        <Button type="button" className="btn btn-primary btn-block  app-add-button" onClick={onGenerateHandler}>
                          GENERATE
                        </Button>
                      </div>
                    </Col>
                    <Col sm="2">
                      <div className="text-sm-end">
                        <CSVLink
                          data={data}
                          headers={exportHeader}
                          filename={"clientlisting.csv"}
                          className="btn btn-primary download-button"
                        >
                          <FontAwesomeIcon icon={faFileExcel} /> EXPORT
                        </CSVLink>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col xl="12">
                      {isLoading ? (
                        <LoadingSpinner />
                      ) : (
                        <ReportViewer>
                          <Document title={reportHeading}>
                            <Page style={generalStyles.body} orientation="landscape">
                              <ReportHeader reportTitle={reportHeading} />
                              <ReportFilterInfo filterList={rptData.filterOptions.filterBy} />
                              <View style={generalStyles.table}>
                                <View style={generalStyles.tableRow}>
                                  <View style={[generalStyles.tableColHeader, styles.clientCode]}>
                                    <Text style={generalStyles.tableCellHeader}>Code</Text>
                                  </View>
                                  <View style={[generalStyles.tableColHeader, styles.clientName]}>
                                    <Text style={generalStyles.tableCellHeader}>Client Name</Text>
                                  </View>
                                  <View style={[generalStyles.tableColHeader, styles.clientTypeName]}>
                                    <Text style={generalStyles.tableCellHeader}>Client Type</Text>
                                  </View>
                                  <View style={[generalStyles.tableColHeader, styles.abn]}>
                                    <Text style={generalStyles.tableCellHeader}>ABN</Text>
                                  </View>
                                  <View style={[generalStyles.tableColHeader, styles.debtorCode]}>
                                    <Text style={generalStyles.tableCellHeader}>Debtor Code</Text>
                                  </View>
                                  <View style={[generalStyles.tableColHeader, styles.deliveryCharge]}>
                                    <Text style={generalStyles.tableCellHeader}>Delivery Charge</Text>
                                  </View>
                                  <View style={[generalStyles.tableColHeader, styles.primaryContactPerson]}>
                                    <Text style={generalStyles.tableCellHeader}>Contact Person</Text>
                                  </View>
                                  <View style={[generalStyles.tableColHeader, styles.primaryContactEmail]}>
                                    <Text style={generalStyles.tableCellHeader}>Contact Email</Text>
                                  </View>

                                  <View style={[generalStyles.tableColHeader, styles.status]}>
                                    <Text style={generalStyles.tableCellHeader}>Status</Text>
                                  </View>
                                </View>
                                {data.length > 0 && <ReportDataRow results={data} />}
                              </View>
                              <ReportFooter footerInfo={reportHeading}>
                                <Text
                                  style={generalStyles.pageNumber}
                                  render={({ pageNumber, totalPages }) => `Page ${pageNumber} of  ${totalPages}`}
                                />
                              </ReportFooter>
                            </Page>
                          </Document>
                        </ReportViewer>
                      )}
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

const ReportDataRow = ({ results }) => {
  return (
    <View>
      {results.map(item => {
        return (
          // <reportDataRow item={n} key={n.clientProfileId}/>
          <View style={generalStyles.tableRow} key={item.clientProfileId}>
            <View style={[generalStyles.tableCol, styles.clientCode]}>
              <Text style={generalStyles.tableCell}>{item.clientCode}</Text>
            </View>
            <View style={[generalStyles.tableCol, styles.clientName]}>
              <Text style={generalStyles.tableCell}> {item.clientName} </Text>
            </View>
            <View style={[generalStyles.tableCol, styles.clientTypeName]}>
              <Text style={generalStyles.tableCell}>{item.clientTypeName}</Text>
            </View>
            <View style={[generalStyles.tableCol, styles.abn]}>
              <Text style={generalStyles.tableCell}>{item.abn}</Text>
            </View>
            <View style={[generalStyles.tableCol, styles.debtorCode]}>
              <Text style={generalStyles.tableCell}>{item.debtorCode}</Text>
            </View>
            <View style={[generalStyles.tableCol, styles.deliveryCharge]}>
              <Text style={generalStyles.tableCell}>{item.deliveryCharge}</Text>
            </View>
            <View style={[generalStyles.tableCol, styles.primaryContactPerson]}>
              <Text style={generalStyles.tableCell}>{item.primaryContactPerson}</Text>
            </View>
            <View style={[generalStyles.tableCol, styles.primaryContactEmail]}>
              <Text style={generalStyles.tableCell}>{item.primaryContactEmail}</Text>
            </View>
            <View style={[generalStyles.tableCol, styles.status]}>
              <Text style={generalStyles.tableCell}>{item.isActive}</Text>
            </View>
          </View>
        );
      })}
    </View>
  );
};

export default ClientProfileListingRpt;
